/**
 * cart.js — selection source of truth for the results page.
 * In-memory state mirrored to sessionStorage; notifies subscribers on change.
 * Plan ids are kept as strings (LifeX numeric, API string) — never parseInt.
 */

const STORAGE_KEY = 'wqCart';
let items = load();            // Array<{key, plan, variantId, premium}>
const listeners = new Set();

function hasStorage() {
  try { return typeof sessionStorage !== 'undefined'; } catch (e) { return false; }
}
function load() {
  if (!hasStorage()) return [];
  try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || []; } catch (e) { return []; }
}
function persist() {
  if (hasStorage()) {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch (e) { /* quota */ }
  }
}
function notify() {
  persist();
  listeners.forEach((fn) => { try { fn(items); } catch (e) { console.error('[Cart] listener error', e); } });
}

export const Cart = {
  init() { items = load(); return Cart; },
  keyFor(plan, variant) { return String(variant && variant.planId != null ? variant.planId : plan.id); },
  add(plan, variant, groupKey, activeRiders) {
    const key = Cart.keyFor(plan, variant);
    const gk = groupKey != null ? String(groupKey) : null;
    const before = items.length;
    // Group-level dedup: at most one variant per group — evict any sibling. (This is also how a
    // rider toggle updates an in-cart item: re-add the same variant with new riders → old evicted.)
    if (gk != null) items = items.filter((i) => i.groupKey !== gk);
    if (items.some((i) => i.key === key)) { if (items.length !== before) notify(); return; }
    // Effective premium = base variant premium + active optional-rider premiums (#4). Riders are
    // stored on the item so the cart line can mirror the toggles and a saved quote keeps the total.
    const base = Number(variant ? variant.premium : plan.calculatedPremium) || 0;
    const riders = Array.isArray(activeRiders) ? activeRiders : [];
    const riderTotal = riders.reduce((s, r) => s + (Number(r && r.premium) || 0), 0);
    items.push({
      key,
      groupKey: gk,
      plan: variant && variant.plan ? variant.plan : plan,
      variantId: variant ? String(variant.planId) : String(plan.id),
      premium: base + riderTotal,
      activeRiders: riders,
    });
    notify();
  },
  remove(key) {
    const k = String(key);
    const before = items.length;
    items = items.filter((i) => i.key !== k);
    if (items.length !== before) notify();
  },
  hasGroup(groupKey) { return items.some((i) => i.groupKey === String(groupKey)); },
  removeGroup(groupKey) {
    const gk = String(groupKey);
    const before = items.length;
    items = items.filter((i) => i.groupKey !== gk);
    if (items.length !== before) notify();
  },
  toggle(plan, variant) {
    const key = Cart.keyFor(plan, variant);
    if (Cart.has(key)) Cart.remove(key); else Cart.add(plan, variant);
  },
  has(key) { return items.some((i) => i.key === String(key)); },
  items() { return items.slice(); },
  /**
   * Returns the stored plan objects for the cart's selections.
   * Each is the same object reference originally added — when a variant was
   * supplied to `add()`, this is `variant.plan` (from `allPlansWithPremiums`),
   * otherwise the bare plan. Suitable as a drop-in replacement for the legacy
   * `allPlansWithPremiums.filter((p) => selectedPlansForEmail.has(p.id))`.
   * @returns {Array<object>} stored plan objects (shared references, treat read-only)
   */
  plans() { return items.map((i) => i.plan); },
  count() { return items.length; },
  combinedMonthly() { return items.reduce((sum, i) => sum + (Number(i.premium) || 0), 0); },
  clear() { if (items.length) { items = []; notify(); } else { items = []; persist(); } },
  subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
};
